// ============================================================================
// FILE: pages/Campaigns/Lead/ListLeads.js (UPDATED - Single Source Only)
// ============================================================================

import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import { Tooltip } from '@mui/material';
import CreateEditLeadModal from "./CreateEditLeadModal";

export default function ListLeads({ refreshTrigger, chatbotId, activeSource, permission }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    const fetchLeads = useCallback(async (page = currentPage, pageSize = perPage, search = searchText) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axiosInstance.get(`/api/leads`, {
                params: {
                    ...params,
                    page: page,
                    per_page: pageSize,
                    search: search
                }
            });
            
            if (response.data.success) {
                setLeads(response.data.data);
                setTotalRows(response.data.pagination.total);
            } else {
                setError(response.data.message || "Failed to fetch leads");
            }
        } catch (err) {
            console.error("Error fetching leads:", err);
            setError(err.response?.data?.message || "Error fetching leads.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, searchText, chatbotId]);

    useEffect(() => {
        fetchLeads();
    }, [refreshTrigger, activeSource]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchText !== undefined) {
                setCurrentPage(1);
                fetchLeads(1, perPage, searchText);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchText]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchLeads(page, perPage, searchText);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchLeads(page, newPerPage, searchText);
    };

    const handleCreateLead = () => {
        setCurrentLead(null);
        setIsCreateModalOpen(true);
    };

    const handleEditLead = (lead) => {
        setCurrentLead(lead);
        setIsEditModalOpen(true);
    };

    const handleDeleteLead = async (lead) => {
        const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
        if (!window.confirm(`Are you sure you want to delete lead "${leadName}"? This action cannot be undone.`)) {
            return;
        }

        setIsDeleting(true);
        setError(null);
        
        try {
            await axiosInstance.delete(`/api/leads/${lead.source_id || lead.id}`, { 
                params: {
                    ...params,
                    source: lead.source_type
                }
            });
            setError("Lead deleted successfully.");
            fetchLeads();
        } catch (err) {
            console.error(`Error deleting lead:`, err);
            setError(err.response?.data?.message || `Error deleting lead.`);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleModalClose = () => {
        setIsCreateModalOpen(false);
        setIsEditModalOpen(false);
        setCurrentLead(null);
    };

    const handleModalSuccess = () => {
        handleModalClose();
        fetchLeads();
        setError("Operation completed successfully.");
    };

    const getSourceBadge = (sourceType) => {
        if (sourceType === 'salesforce') {
            return <span className="badge bg-primary"><i className="fa fa-cloud me-1"></i>Salesforce</span>;
        } else {
            return <span className="badge bg-success"><i className="fa fa-user-circle me-1"></i>Customer Portal</span>;
        }
    };

    const getStatusBadgeClass = (status) => {
        const statusMap = {
            'New': 'bg-info',
            'Open - Not Contacted': 'bg-primary',
            'Working - Contacted': 'bg-warning',
            'Closed - Converted': 'bg-success',
            'Closed - Not Converted': 'bg-secondary',
            'Qualified': 'bg-success',
            'Unqualified': 'bg-danger'
        };
        return statusMap[status] || 'bg-secondary';
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: "Company",
            selector: (row) => row.company,
            sortable: true,
            wrap: true
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
            wrap: true
        },
        {
            name: "Phone",
            selector: (row) => row.phone,
            sortable: true
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span className={`badge ${getStatusBadgeClass(row.status)}`}>
                    {row.status}
                </span>
            )
        },
        {
            name: "Actions",
            width: '150px',
            cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
                    {permission.canModify && (

                        <Tooltip title="Edit Lead" arrow>
                            <button 
                                className="action-icon" 
                                onClick={() => handleEditLead(row)}
                                disabled={loading || isDeleting}
                            >
                                <i className="fa fa-pencil"></i>
                            </button>
                        </Tooltip>
                    )}

                    {permission.canDelete && (

                        <Tooltip title="Delete Lead" arrow>
                            <button 
                                className="action-icon delete" 
                                onClick={() => handleDeleteLead(row)}
                                disabled={loading || isDeleting}
                            >
                                <i className="fa fa-trash-o"></i>
                            </button>
                        </Tooltip>

                    )}

                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const ExpandedComponent = ({ data }) => (
        <div style={{ padding: '15px', borderLeft: '3px solid #0d6efd', backgroundColor: '#f8f9fa', fontSize: '0.9em' }}>
            <div className="row">
                <div className="col-md-6">
                    <p><strong>Lead ID:</strong> {data.source_id || data.id}</p>
                    <p><strong>Source:</strong> {getSourceBadge(data.source_type)}</p>
                    <p><strong>Name:</strong> {`${data.first_name || ''} ${data.last_name || ''}`.trim()}</p>
                    <p><strong>Title:</strong> {data.title || 'N/A'}</p>
                    <p><strong>Company:</strong> {data.company || 'N/A'}</p>
                    <p><strong>Email:</strong> {data.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {data.phone || 'N/A'}</p>
                    <p><strong>Website:</strong> {data.website || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                    <p><strong>Status:</strong> {data.status || 'N/A'}</p>
                    <p><strong>Address:</strong></p>
                    <p style={{ marginLeft: '15px' }}>
                        {data.street || ''}<br />
                        {data.city ? `${data.city}, ` : ''}{data.state || ''} {data.postal_code || ''}<br />
                        {data.country || ''}
                    </p>
                    <p><strong>Description:</strong> {data.description || 'N/A'}</p>
                    <p><strong>Created:</strong> {data.created_at ? new Date(data.created_at).toLocaleString() : 'N/A'}</p>
                    <p><strong>Last Updated:</strong> {data.updated_at ? new Date(data.updated_at).toLocaleString() : 'N/A'}</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="mt-3">
            <div className="row mb-3 align-items-end">
                <div className="col-md-6">
                    {permission.canModify && (
                        <button
                            className="btn btn-primary"
                            onClick={handleCreateLead}
                            disabled={loading}
                        >
                            <i className="fa fa-plus me-2"></i>Create New Lead
                        </button>
                    )}
                </div>
                <div className="col-md-6">
                    <input
                        type="text"
                        placeholder="Search by name, company, or email..."
                        className="form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            {error && (
                <div className={`alert ${error.toLowerCase().includes("error") || error.toLowerCase().includes("failed") ? "alert-danger" : "alert-success"} my-2 alert-dismissible fade show`} role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                </div>
            )}

            <DataTable
                columns={columns}
                data={leads}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                responsive
                highlightOnHover
                pointerOnHover
                expandableRows
                expandableRowsComponent={ExpandedComponent}
            />

            {/* Create Lead Modal */}
            {isCreateModalOpen && (
                <CreateEditLeadModal
                    isOpen={isCreateModalOpen}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                    lead={null}
                    mode="create"
                    chatbotId={chatbotId}
                    activeSource={activeSource}
                    permission={permission}
                />
            )}

            {/* Edit Lead Modal */}
            {isEditModalOpen && currentLead && (
                <CreateEditLeadModal
                    isOpen={isEditModalOpen}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                    lead={currentLead}
                    mode="edit"
                    chatbotId={chatbotId}
                    activeSource={activeSource}
                    permission={permission}
                />
            )}

            <style jsx global>{`
                .action-icon { 
                    background: none; 
                    border: none; 
                    padding: 5px; 
                    cursor: pointer; 
                    color: #0d6efd; 
                    font-size: 1.1em; 
                }
                .action-icon.delete { color: #dc3545; }
                .action-icon:hover { opacity: 0.7; }
                .action-icon:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
        </div>
    );
}